/**
 * [T-065] Reports — contracts (tasks/W7-CONTRACTS.md §5 นิยามตัวเลขตายตัว)
 * - ทุกช่วงเวลาเป็น [from, to) ปลายเปิด ตามเขตเวลา Asia/Bangkok (วันเริ่ม 00:00 ไทย) — response กำกับ `range` เสมอ
 * - median/p90 ไม่ใช่ average: median = percentile_cont(0.5) (interpolate) · p90 = percentile_disc(0.9) (nearest-rank ตามตัวอย่างใบงาน 5/10/100 → 10/100)
 * - เวลาทำการ (business) มาจาก ticket_state_periods.business_seconds — nullable เสมอ พร้อมบอก source และจำนวนแถวที่หาไม่ได้ (unknown ≠ 0)
 */
import { z } from "zod";

export const REPORT_TZ = "Asia/Bangkok";

export const ReportGranularity = z.enum(["day", "week", "month"]);
export type ReportGranularity = z.infer<typeof ReportGranularity>;

/** ชนิดรายงานที่เปิดให้ query ได้ (agents มีข้อจำกัดสิทธิ์เพิ่ม — W7 §5) */
export const ReportKind = z.enum(["overview", "volume", "response-times", "time-in-status", "backlog", "csat", "agents"]);
export type ReportKind = z.infer<typeof ReportKind>;

/** ชนิดรายงานที่ตั้ง schedule ส่งอีเมลได้ — ไม่รวม agents (ข้อมูลรายบุคคลออกอีเมลอัตโนมัติ = ข้ามกลไกสิทธิ์/flag) */
export const ReportScheduleKind = z.enum(["overview", "volume", "response-times", "time-in-status", "backlog", "csat"]);
export type ReportScheduleKind = z.infer<typeof ReportScheduleKind>;

/** query ร่วมของทุก endpoint (from/to = วันที่ YYYY-MM-DD เขตเวลาไทย · ค่าเริ่มต้น 30 วันล่าสุด) */
export const ReportQuery = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  team_id: z.uuid().optional(),
  service_id: z.uuid().optional(),
  department_id: z.uuid().optional(),
  granularity: ReportGranularity.optional(),
});
export type ReportQuery = z.infer<typeof ReportQuery>;

/** กำกับทุก response — ให้คนอ่านรู้ว่านับยังไง ([from,to) · วันไทย) */
export const ReportRange = z.object({ from: z.string(), to: z.string(), tz: z.literal(REPORT_TZ) });
export type ReportRange = z.infer<typeof ReportRange>;

/** ขอบเขตที่ผู้เรียงเห็นจริงหลังผ่านกติกาสิทธิ์ (team_ids = null คือทั้งองค์กร) */
export const ReportScope = z.object({
  team_ids: z.array(z.uuid()).nullable(),
  department_ids: z.array(z.uuid()),
  own_only: z.boolean(),
});
export type ReportScope = z.infer<typeof ReportScope>;

/** สถิติเวลาแบบ wall-clock (นาที) — null เมื่อไม่มีข้อมูล */
export const LatencyStat = z.object({
  median_minutes: z.number().nullable(),
  p90_minutes: z.number().nullable(),
  count: z.number().int(),
});
export type LatencyStat = z.infer<typeof LatencyStat>;

/** เวลาทำการ — nullable พร้อมบอกที่มา + จำนวนที่ไม่มี business_seconds (unknown ≠ 0) */
export const BusinessLatencyStat = z.object({
  median_minutes: z.number().nullable(),
  p90_minutes: z.number().nullable(),
  count: z.number().int(),
  business: z.object({
    median_minutes: z.number().nullable(),
    p90_minutes: z.number().nullable(),
    source: z.literal("ticket_state_periods.business_seconds"),
    available: z.boolean(),
    unavailable_count: z.number().int(),
    total_count: z.number().int(),
  }),
});
export type BusinessLatencyStat = z.infer<typeof BusinessLatencyStat>;

const ReportEnvelope = z.object({ range: ReportRange, scope: ReportScope });

export const ReportOverviewNumbers = z.object({
  created: z.number().int(),
  resolved: z.number().int(),
  backlog_now: z.number().int(),
  frt: BusinessLatencyStat,
  resolution: LatencyStat,
  csat: z.object({ count: z.number().int(), avg: z.number().nullable(), pct_satisfied: z.number().nullable() }),
  bounces: z.object({ bounced: z.number().int(), complained: z.number().int() }),
});
export type ReportOverviewNumbers = z.infer<typeof ReportOverviewNumbers>;

/** GET /api/v1/reports/overview — ตัวเลขสรุปชุดเดียว + เทียบช่วงก่อนหน้าเท่า ๆ กัน */
export const ReportOverview = ReportEnvelope.extend({
  current: ReportOverviewNumbers,
  previous: ReportOverviewNumbers.nullable(),
  delta: ReportOverviewNumbers.nullable(),
});
export type ReportOverview = z.infer<typeof ReportOverview>;

/** GET /api/v1/reports/volume — อนุกรมเวลา เข้า/ปิด ต่อ bucket (วันไทย · week = จันทร์) */
export const ReportVolume = ReportEnvelope.extend({
  granularity: ReportGranularity,
  buckets: z.array(
    z.object({
      bucket: z.string(),
      created: z.number().int(),
      resolved: z.number().int(),
    }),
  ),
  totals: z.object({ created: z.number().int(), resolved: z.number().int() }),
});
export type ReportVolume = z.infer<typeof ReportVolume>;

/** GET /api/v1/reports/response-times — FRT + เวลาปิด median/p90 (wall + business) */
export const ReportResponseTimes = ReportEnvelope.extend({
  frt: BusinessLatencyStat,
  resolution: LatencyStat,
});
export type ReportResponseTimes = z.infer<typeof ReportResponseTimes>;

/** GET /api/v1/reports/time-in-status — เวลารวมต่อสถานะ (median ต่อการ์ด · clamp เข้าช่วง) */
export const ReportTimeInStatus = ReportEnvelope.extend({
  items: z.array(
    z.object({
      status_id: z.uuid(),
      status_code: z.string(),
      status_name: z.string(),
      tickets: z.number().int(),
      median_minutes: z.number().nullable(),
      p90_minutes: z.number().nullable(),
      total_seconds: z.number().int(),
    }),
  ),
});
export type ReportTimeInStatus = z.infer<typeof ReportTimeInStatus>;

/** GET /api/v1/reports/backlog — งานค้าง ณ ตอนนี้ แบ่งช่วงอายุ (วันเต็ม · ไม่ซ้อนกัน) */
export const ReportBacklog = z.object({
  as_of: z.string(),
  range: ReportRange,
  scope: ReportScope,
  total: z.number().int(),
  buckets: z.array(
    z.object({
      key: z.string(),
      min_days: z.number().int(),
      max_days: z.number().int().nullable(),
      count: z.number().int(),
    }),
  ),
  oldest_days: z.number().nullable(),
});
export type ReportBacklog = z.infer<typeof ReportBacklog>;

/** GET /api/v1/reports/csat — จำนวน/เฉลี่ย/%พอใจ (4-5) แยกตามทีม */
export const ReportCsat = ReportEnvelope.extend({
  totals: z.object({ count: z.number().int(), avg: z.number().nullable(), pct_satisfied: z.number().nullable() }),
  by_team: z.array(
    z.object({
      team_id: z.uuid().nullable(),
      team_code: z.string().nullable(),
      team_name: z.string().nullable(),
      count: z.number().int(),
      avg: z.number().nullable(),
      pct_satisfied: z.number().nullable(),
    }),
  ),
});
export type ReportCsat = z.infer<typeof ReportCsat>;

/** GET /api/v1/reports/agents — ภาระงานรายคน (admin + หัวหน้าแผนก + flag reports.per_agent) */
export const ReportAgents = ReportEnvelope.extend({
  /** merged ฝั่งใบรองที่ไม่มี data.was_closed — ตัดไปคนใดไม่ได้ (unknown ≠ 0) */
  unknown_merged_count: z.number().int(),
  /** การปิดการ์ดโดย actor ที่ไม่ใช่ user (automation/system) — ไม่ผูกกับคน */
  non_user_closed_count: z.number().int(),
  /** ป้ายกำกับวิธีตัดสินผู้ปิดการ์ดตาม W7 §5 (T-068 ใช้ render คำอธิบาย) */
  close_attribution: z.object({
    transitions: z.literal("exact"),
    merged: z.literal("exact_for_was_closed_false"),
    unknown_merged_count: z.number().int(),
  }),
  /** การกรองทีมของ events/ข้อความเก่าใช้ holder_team_id ปัจจุบันของการ์ด (W7 §5) */
  team_attribution: z.literal("current_holder"),
  agents: z.array(
    z.object({
      user_id: z.uuid(),
      display_name: z.string().nullable(),
      primary_email: z.string().nullable(),
      closed_count: z.number().int(),
      merged_closed_count: z.number().int(),
      public_replies: z.number().int(),
    }),
  ),
});
export type ReportAgents = z.infer<typeof ReportAgents>;

// ---- schedules (R5) -------------------------------------------------------------

export const ReportScheduleFilters = z.object({
  team_id: z.uuid().optional(),
  service_id: z.uuid().optional(),
  department_id: z.uuid().optional(),
  granularity: ReportGranularity.optional(),
});
export type ReportScheduleFilters = z.infer<typeof ReportScheduleFilters>;

export const ReportScheduleReport = z.object({ type: ReportScheduleKind, filters: ReportScheduleFilters });
export type ReportScheduleReport = z.infer<typeof ReportScheduleReport>;

export const ReportScheduleCreate = z
  .object({
    name: z.string().trim().min(1).max(120),
    report: ReportScheduleReport,
    cadence: z.enum(["daily", "weekly", "monthly"]),
    hour_utc: z.number().int().min(0).max(23),
    weekday: z.number().int().min(0).max(6).nullable().optional(),
    day_of_month: z.number().int().min(1).max(31).nullable().optional(),
    /** อีเมลผู้รับ — ทุกคนต้องเป็น staff ตอนบันทึก (R5) */
    recipients: z.array(z.email()).min(1).max(50),
    format: z.enum(["summary", "csv"]).default("summary"),
    enabled: z.boolean().default(true),
  })
  .check((ctx) => {
    const v = ctx.value;
    if (v.cadence === "weekly" && v.weekday === undefined) {
      ctx.issues.push({ code: "custom", input: v, path: ["weekday"], message: "weekly ต้องระบุ weekday (0=อาทิตย์)" });
    }
    if (v.cadence === "monthly" && v.day_of_month === undefined) {
      ctx.issues.push({ code: "custom", input: v, path: ["day_of_month"], message: "monthly ต้องระบุ day_of_month" });
    }
    if (v.cadence === "daily" && (v.weekday !== undefined || v.day_of_month !== undefined)) {
      ctx.issues.push({ code: "custom", input: v, path: ["cadence"], message: "daily ต้องไม่ระบุ weekday/day_of_month" });
    }
  });
export type ReportScheduleCreate = z.infer<typeof ReportScheduleCreate>;

export const ReportSchedulePatch = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    report: ReportScheduleReport.optional(),
    cadence: z.enum(["daily", "weekly", "monthly"]).optional(),
    hour_utc: z.number().int().min(0).max(23).optional(),
    weekday: z.number().int().min(0).max(6).nullable().optional(),
    day_of_month: z.number().int().min(1).max(31).nullable().optional(),
    recipients: z.array(z.email()).min(1).max(50).optional(),
    format: z.enum(["summary", "csv"]).optional(),
    enabled: z.boolean().optional(),
  })
  .check((ctx) => {
    const v = ctx.value;
    // merged patch + row ปัจจุบันต้องเป็นชุดที่สอดคล้องกัน — ตรวจที่ route (ต้องอ่านแถวเดิม) ที่นี่เช็คเฉพาะค่าที่ส่งมาเองขัดกันเอง
    // [T-068 B1 · Lead-sanctioned] explicit null = ล้าง field (สลับ cadence) — ห้ามเป็นแค่ undefined-check เพราะตัดสิทธิ์การล้างค่า
    if (v.cadence === "daily" && (v.weekday != null || v.day_of_month != null)) {
      ctx.issues.push({ code: "custom", input: v, path: ["cadence"], message: "daily ต้องไม่ระบุ weekday/day_of_month" });
    }
  });
export type ReportSchedulePatch = z.infer<typeof ReportSchedulePatch>;

export const ReportSchedule = z.object({
  id: z.uuid(),
  name: z.string(),
  report: ReportScheduleReport,
  cadence: z.enum(["daily", "weekly", "monthly"]),
  hour_utc: z.number().int(),
  weekday: z.number().int().nullable(),
  day_of_month: z.number().int().nullable(),
  recipients: z.array(z.string()),
  format: z.enum(["summary", "csv"]),
  team_id: z.uuid().nullable(),
  enabled: z.boolean(),
  created_by: z.uuid(),
  last_run_at: z.string().nullable(),
  created_at: z.string(),
});
export type ReportSchedule = z.infer<typeof ReportSchedule>;
